import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, TextField, Button, IconButton, Modal,
    Typography, Paper, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import Cookies from 'universal-cookie';

const getHeaders = () => {
    const cookies = new Cookies();
    return { headers: { Authorization: `Bearer ${cookies.get('token')}` } };
};

const TenantPage = () => {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterText, setFilterText] = useState('');
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [tenantToDelete, setTenantToDelete] = useState(null);

    const [name, setName] = useState('');
    const [identifier, setIdentifier] = useState('');

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(window.API_URL + '/GetAllTenants', getHeaders());
            const raw = response.data;
            const list = Array.isArray(raw) ? raw : (raw?.$values || raw?.items || raw?.data || []);
            setTenants(list);
        } catch (error) {
            console.error('Error fetching tenants:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const resetForm = () => {
        setName('');
        setIdentifier('');
    };

    const handleAdd = () => {
        setSelectedTenant(null);
        resetForm();
        setIsModalOpen(true);
    };

    const handleEdit = (tenant) => {
        setSelectedTenant(tenant);
        setName(tenant.name || '');
        setIdentifier(tenant.identifier || '');
        setIsModalOpen(true);
    };

    const handleDelete = (tenant) => {
        setTenantToDelete(tenant);
        setIsDeleteDialogOpen(true);
    };

    const handleSave = async () => {
        const dto = { name, identifier: identifier || null, active: true };
        try {
            if (selectedTenant) {
                await axios.put(
                    `${window.API_URL}/UpdateTenant/${selectedTenant.id}`,
                    dto,
                    getHeaders()
                );
            } else {
                await axios.post(`${window.API_URL}/CreateTenant`, dto, getHeaders());
            }
            setIsModalOpen(false);
            loadData();
        } catch (error) {
            console.error('Error saving tenant:', error);
        }
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(
                `${window.API_URL}/DeleteTenant/${tenantToDelete.id}`,
                getHeaders()
            );
            setIsDeleteDialogOpen(false);
            loadData();
        } catch (error) {
            console.error('Error deleting tenant:', error);
        }
    };

    const columns = [
        {
            name: 'ID',
            selector: row => row.id,
            sortable: true,
            maxWidth: '80px',
        },
        {
            name: 'Nombre',
            selector: row => row.name,
            sortable: true,
            grow: 2,
        },
        {
            name: 'Identifier',
            selector: row => row.identifier || '-',
            sortable: true,
        },
        {
            name: 'Acciones',
            cell: row => (
                <Box>
                    <IconButton onClick={() => handleEdit(row)} size="small" className="apple-button apple-tooltip">
                        <EditIcon fontSize="small" />
                        <span className="tooltiptext">Editar</span>
                    </IconButton>
                    <IconButton onClick={() => handleDelete(row)} size="small" className="apple-button apple-tooltip">
                        <RemoveCircleOutlineIcon fontSize="small" color="error" />
                        <span className="tooltiptext">Eliminar</span>
                    </IconButton>
                </Box>
            ),
            width: '120px',
        },
    ];

    const customStyles = {
        headRow: { style: { backgroundColor: '#F2F2F7', borderBottom: 'none' } },
        headCells: { style: { fontSize: '12px', fontWeight: '600', color: '#86868B', textTransform: 'uppercase' } },
        cells: { style: { fontSize: '14px', color: '#1D1D1F' } },
    };

    const filteredItems = tenants.filter(
        t => (t.name || '').toLowerCase().includes(filterText.toLowerCase()) ||
             (t.identifier || '').toLowerCase().includes(filterText.toLowerCase())
    );

    const subHeaderComponent = useMemo(() => (
        <Box className="apple-card" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
            <div className="apple-search-container">
                <SearchIcon className="apple-search-icon" />
                <input
                    type="text"
                    placeholder="Buscar por nombre o identifier"
                    onChange={e => setFilterText(e.target.value)}
                    className="apple-search-input"
                />
            </div>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} className="apple-button">
                Agregar Tenant
            </Button>
        </Box>
    ), [filterText]);

    return (
        <div className="apple-container">
            <Paper className="apple-card">
                <DataTable
                    columns={columns}
                    data={filteredItems}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 30]}
                    subHeader
                    subHeaderComponent={subHeaderComponent}
                    progressPending={loading}
                    persistTableHead
                    customStyles={customStyles}
                />
            </Paper>

            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <Box className="apple-modal" sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: 480,
                }}>
                    <Typography className="apple-modal-title">
                        {selectedTenant ? 'Editar Tenant' : 'Agregar Tenant'}
                    </Typography>
                    <div className="apple-modal-content">
                        <TextField
                            label="Nombre *"
                            variant="outlined"
                            fullWidth
                            className="apple-input"
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                        <TextField
                            label="Identifier"
                            variant="outlined"
                            fullWidth
                            className="apple-input"
                            value={identifier}
                            onChange={e => setIdentifier(e.target.value)}
                        />
                    </div>
                    <div className="apple-modal-footer">
                        <Button onClick={() => setIsModalOpen(false)} className="apple-button">
                            Cancelar
                        </Button>
                        <Button variant="contained" className="apple-button" onClick={handleSave} disabled={!name}>
                            {selectedTenant ? 'Guardar Cambios' : 'Crear Tenant'}
                        </Button>
                    </div>
                </Box>
            </Modal>

            <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el tenant <strong>{tenantToDelete?.name}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)} className="apple-button">Cancelar</Button>
                    <Button onClick={confirmDelete} className="apple-button" autoFocus>Confirmar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default TenantPage;
